import { z } from "zod";
import {
  isChannelRestrictionType,
  isWorkspaceRestrictionType,
} from "./TicketDefaultDocRestrictionType";

const translationPath = "page.settings.crm.protocolsDefault.errors.";

const mediaFileSchema = z.object(
  {
    id: z.string().min(1, translationPath + "file"),
    mediaTypeId: z.string().optional(),
    isPublic: z.boolean().optional(),
    fileName: z.string().optional(),
    cdnDomain: z.string().optional(),
    filePath: z.string().optional(),
    mimeType: z.string().optional(),
    fileSize: z.number().optional(),
  },
  { required_error: translationPath + "file" }
);

const contentMediaFilesSchema = z
  .object({
    file: mediaFileSchema,
    description: z
      .string()
      .min(1, translationPath + "min_description")
      .max(1000, translationPath + "max_description"),
  })
  .superRefine((val, ctx) => {
    if (val.file.id.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["file"],
        message: translationPath + "file",
      });
    }
  });

const ticketWorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
});

const ticketChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
});

const tickeAutomationSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
});

const ticketDefaultDocButtonTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
});

const ticketDefaultDocCategorySchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .nullish()
  .refine((value) => !!value, {
    message: translationPath + "category",
  });

const TicketDefaultDocButtonSchema = z.object({
  id: z.string().optional().nullable(),
  label: z
    .string()
    .trim()
    .min(1, translationPath + "label"),
  value: z.string().nullish(),
});

export const protocolDefaultSchema = z
  .object({
    id: z.string().optional(),
    enable: z.boolean(),
    title: z.string().min(1, translationPath + "title"),
    message: z.string().min(1, translationPath + "message"),
    ticketAutomation: tickeAutomationSchema.nullish(),
    ticketDefaultDocButtonType: ticketDefaultDocButtonTypeSchema.nullish(),
    restrictionType: z.string().min(1, translationPath + "restriction_type"),
    ticketDefaultDocCategory: ticketDefaultDocCategorySchema,
    ticketWorkspaces: z.array(ticketWorkspaceSchema).optional(),
    ticketChannels: z.array(ticketChannelSchema).optional(),
    ticketDefaultDocButtons: z.array(TicketDefaultDocButtonSchema).nullish(),
    ticketDefaultDocMediaFiles: z
      .array(contentMediaFilesSchema)
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      isWorkspaceRestrictionType(data.restrictionType) &&
      (data.ticketWorkspaces ?? []).length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ticketWorkspaces"],
        message: translationPath + "workspaces",
      });
    }

    if (
      isChannelRestrictionType(data.restrictionType) &&
      (data.ticketChannels ?? []).length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ticketChannels"],
        message: translationPath + "channels",
      });
    }

    const btnType = data.ticketDefaultDocButtonType;
    const buttons = data.ticketDefaultDocButtons ?? [];
    if (btnType?.code === "2") {
      const urlRegex = /^https?:\/\/.+/i;

      buttons.forEach((b, idx) => {
        const value = b.value ?? "";
        if (typeof value !== "string" || !urlRegex.test(value.trim())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ticketDefaultDocButtons", idx, "value"],
            message: translationPath + "button_link_required",
          });
        }
      });
    }
  });

export type TProtocolDefaultForm = z.infer<typeof protocolDefaultSchema>;
